import { Box, BoxProps, Flex, Icon, Text, useToast } from "@chakra-ui/react";
import React, { useMemo } from "react";
import { HiOutlineClipboardList } from "react-icons/hi";
import {
  HiChevronDoubleLeft,
  HiChevronDoubleRight,
  HiOutlineSparkles,
} from "react-icons/hi2";

import { Button, EmptyAIIcon } from "../../../../components";
import useCurrentBreakpoint from "../../../../hooks/useCurrentBreakpoint";
import { useIsSmallScreen } from "../../../../hooks/useIsSmallScreen";
import useWindowDimensions from "../../../../hooks/useWindowDimensions";
import { copy } from "../../../../utils/clipboard";
import { useSendGAEvent } from "../../../../utils/googleAnalytics";
import {
  CandidateSummaryProcessingStatus,
  CandidateSummaryTemplateFragment,
  CandidateSummaryTemplateStatus,
} from "../../../graphql";
import { useIsExtension } from "../../../hooks/useAppEnvironmentContext";
import useTrackOnce from "../../../hooks/useTrackOnce";
import AiNotesLoading from "../../Recording/SidebarTabs/Ai/Notes/NotesQA/AiNotesLoading";
import { ColumnHeaderBorder, ColumnHeading } from "../ColumnHeading";
import Notes from "./Notes";
import SummaryCallCountBanner from "./SummaryCallCountBanner";
import SummaryCopyNotesButton from "./SummaryCopyNotesButton";
import SummaryEditTemplateButton from "./SummaryEditTemplateButton";
import SummaryEmptyStateWrapper from "./SummaryEmptyStateWrapper";
import SummaryGenericTemplateBanner from "./SummaryGenericTemplateBanner";
import SummaryTemplateSelect from "./SummaryTemplateSelect";
import SummaryTooltipIcon from "./SummaryTooltipIcon";
import TopicCoverageList from "./TopicCoverageList";
import TopicsAndNotes from "./TopicsAndNotes";
import { CandidateSummary, Note, SubTopic, Topic } from "./types";
import { formatTopic } from "./utils";

type SummaryTabContentProps = {
  selectedTemplateId: string;
  candidateId: string;
  handleChangeTemplate: (templateId: string) => void;
  data?: CandidateSummary | null;
  dataLoading?: boolean;
  templates: CandidateSummaryTemplateFragment[];
  generateSummary?: (templateId: string) => void;
  notesExpanded: boolean;
  toggleNotes: () => void;
  innerExpandedWidth: string;
  positionId: string | null;
  numCalls: number;
  selectedTemplateInProgress: boolean;
};

const SummaryTabContent: React.FC<SummaryTabContentProps> = ({
  selectedTemplateId,
  candidateId,
  handleChangeTemplate,
  data,
  dataLoading,
  templates,
  generateSummary,
  notesExpanded,
  toggleNotes,
  innerExpandedWidth,
  positionId,
  numCalls,
  selectedTemplateInProgress,
}) => {
  const { windowHeight } = useWindowDimensions();
  const toast = useToast();
  const sendGAEvent = useSendGAEvent();
  const breakpoint = useCurrentBreakpoint();

  const topics = useMemo(() => {
    if (data) {
      return reshapeNotes(data);
    }
  }, [data]);

  const citationMap = useMemo(() => {
    let citationCount = 0;
    const cMap: { [key: string]: number } = {};
    topics?.forEach((topic) => {
      topic.subTopics?.forEach((subTopic) => {
        subTopic.notes?.forEach((note) => {
          note.citations.forEach((citation) => {
            citationCount += 1;
            cMap[citation.id] = citationCount;
          });
        });
      });
    });
    return cMap;
  }, [topics]);

  const formatAllTopics = (
    copyingTopics: Topic[]
  ): { plain: string; html: string } => {
    let plain = "";
    let html = "";

    copyingTopics?.forEach((topic) => {
      const res = formatTopic(topic, citationMap);
      plain += `${res.plain}\n\n`;
      html += `${res.html}<br />`;
    });
    return { plain, html };
  };

  const copyAllTopics = (): void => {
    if (!topics) {
      return;
    }
    const result = formatAllTopics(topics);
    copy({ plain: result.plain, html: result.html });
    toast({
      title: "Debrief copied to clipboard",
      status: "success",
      position: "top",
    });
    sendGAEvent("candidate_summary_copy_all", "call_review");
  };

  const isProcessingStatus = (
    status?: CandidateSummaryProcessingStatus
  ): boolean =>
    status !== CandidateSummaryProcessingStatus.Completed &&
    status !== CandidateSummaryProcessingStatus.Failed;
  const summaryLoading =
    !dataLoading && data && isProcessingStatus(data.status);

  const isExtension = useIsExtension();
  const isSmallScreen = useIsSmallScreen();
  const isSmallish = isSmallScreen || breakpoint === "lg";

  const trackFirstScroll = useTrackOnce("candidate_summary_note_scroll");

  const selectedTemplate = templates.find(
    (template) => template.id === selectedTemplateId
  );
  const selectedTemplateIsDefault = Boolean(selectedTemplate?.isOrgDefault);

  const showingDefaultSummary = Boolean(
    selectedTemplateIsDefault && topics?.length
  );
  const summaryId: string = data?.id || "";

  const selectedTemplateLoading =
    selectedTemplate?.processingStatus ===
    CandidateSummaryTemplateStatus.InProgress;

  const summaryOrTemplateLoading = summaryLoading || selectedTemplateLoading;

  return (
    <Flex
      flexDir="row"
      gap={4}
      height={topics && !isSmallish ? "100%" : undefined}
      maxH={isSmallish ? undefined : parseInt(windowHeight) - 272}
      flex="1"
      overflow="hidden"
    >
      {isExtension || isSmallish ? (
        <CombinedTopicSummaryWrapper
          templates={templates}
          summaryExists={!!topics}
          generateSummary={generateSummary}
          handleChangeTemplate={handleChangeTemplate}
          selectedTemplate={selectedTemplateId}
          copyAll={copyAllTopics}
          candidateId={candidateId}
          notesExpanded={notesExpanded}
          toggleNotes={toggleNotes}
          positionId={positionId}
          isExtension={isExtension}
          summaryOrTemplateLoading={summaryOrTemplateLoading}
        >
          {summaryOrTemplateLoading && (
            <AiSummaryLoading templateLoading={selectedTemplateLoading} />
          )}
          {!summaryOrTemplateLoading && (!topics || topics.length < 1) && (
            <EmptyStateGenerate
              selectedTemplate={selectedTemplateId}
              generateSummary={generateSummary}
              px="10"
            />
          )}
          {topics && !summaryOrTemplateLoading && topics.length > 0 && (
            <>
              <SummaryCallCountBanner
                bannerKey={summaryId}
                numSummaryCalls={data?.calls.length}
                numCurrentVisibleCalls={numCalls}
                mx={0}
              />
              {showingDefaultSummary && (
                <SummaryGenericTemplateBanner bannerKey={summaryId} mx={0} />
              )}
              <Box overflow="auto" mb={2} onScroll={trackFirstScroll}>
                <TopicsAndNotes
                  topics={topics}
                  onClickTimestamp={(callId: string, t: number) => {
                    window.open(`/interview/${callId}?t=${t}`);
                  }}
                />
              </Box>
            </>
          )}
        </CombinedTopicSummaryWrapper>
      ) : (
        <SeparateTopicSummaryWrapper
          templates={templates}
          summaryExists={!!topics}
          generateSummary={generateSummary}
          handleChangeTemplate={handleChangeTemplate}
          selectedTemplate={selectedTemplateId}
          copyAll={copyAllTopics}
          candidateId={candidateId}
          notesExpanded={notesExpanded}
          toggleNotes={toggleNotes}
          positionId={positionId}
          summaryOrTemplateLoading={summaryOrTemplateLoading}
        >
          {summaryOrTemplateLoading && (
            <AiSummaryLoading templateLoading={selectedTemplateLoading} />
          )}
          {!summaryOrTemplateLoading && (!topics || topics.length < 1) && (
            <EmptyStateGenerate
              selectedTemplate={selectedTemplateId}
              generateSummary={generateSummary}
              px="10"
            />
          )}
          {topics && topics.length > 0 && !summaryOrTemplateLoading && (
            <>
              <SummaryCallCountBanner
                bannerKey={summaryId}
                numSummaryCalls={data?.calls.length}
                numCurrentVisibleCalls={numCalls}
              />
              {showingDefaultSummary && (
                <SummaryGenericTemplateBanner bannerKey={summaryId} />
              )}
              <Flex flexDir="row" width={innerExpandedWidth} overflow="auto">
                <TopicCoverageList
                  topics={topics}
                  onNavigate={() => {
                    if (!notesExpanded) {
                      toggleNotes();
                    }
                  }}
                />
                <Box mb={2} onScroll={trackFirstScroll} overflow="auto">
                  <Notes
                    topics={topics}
                    citationMap={citationMap}
                    onClickTimestamp={(callId: string, t: number) => {
                      window.open(`/interview/${callId}?t=${t}`);
                    }}
                  />
                </Box>
              </Flex>
            </>
          )}
        </SeparateTopicSummaryWrapper>
      )}
    </Flex>
  );
};

type AiSummaryLoadingProps = {
  templateLoading: boolean;
};

const AiSummaryLoading: React.FC<AiSummaryLoadingProps> = ({
  templateLoading,
}) => {
  const message = templateLoading
    ? "Generating template"
    : "Generating summary";
  return (
    <Box mb={12}>
      <AiNotesLoading loadingText={message} pt={2} />
    </Box>
  );
};

type TopicSummaryWrapperProps = {
  children: React.ReactNode;
  candidateId: string;
  selectedTemplate: string;
  handleChangeTemplate: (templateId: string) => void;
  templates: CandidateSummaryTemplateFragment[];
  summaryExists?: boolean;
  generateSummary?: (templateId: string) => void;
  copyAll: () => void;
  notesExpanded: boolean;
  toggleNotes: () => void;
  positionId: string | null;
  isExtension?: boolean;
  summaryOrTemplateLoading: boolean;
};

/*
 * Wrapper for V2 candidate summary tab for larger screens.
 * This is for when the topics and notes are displayed in separate columns.
 */
//* *
const SeparateTopicSummaryWrapper = ({
  children,
  candidateId,
  selectedTemplate,
  handleChangeTemplate,
  templates,
  summaryExists,
  copyAll,
  notesExpanded,
  toggleNotes,
  positionId,
  summaryOrTemplateLoading,
}: TopicSummaryWrapperProps): JSX.Element => {
  const currentBreakPoint = useCurrentBreakpoint();
  const isBaseOrSmall = ["base", "sm"].includes(currentBreakPoint);
  const sendGAEvent = useSendGAEvent();
  const currentTemplate = templates.find(
    (item) => item.id === selectedTemplate
  );

  return (
    <Flex
      flexDir="column"
      overflow="hidden"
      onScroll={(e) => {
        // This is to prevent horizontal scrolling that can occur when the user
        // clicks a link in the topic sidepanel. This el should never scroll.
        if (e.currentTarget.scrollLeft > 0) {
          e.currentTarget.scrollLeft = 0;
        }
      }}
      width="100%"
    >
      <Flex
        flexDir="row"
        position="relative"
        alignItems="center"
        px={6}
        zIndex="0"
      >
        <ColumnHeading
          data-tour-id="candidate-summary-column-heading-separate"
          iconType={HiOutlineClipboardList}
          title="Topic coverage"
        />

        <Button
          data-tour-id="candidate-summary-column-expand-summary"
          variant="ghost"
          onClick={() => {
            toggleNotes();
            sendGAEvent(
              `candidate_summary_notes_${
                notesExpanded ? "collapse" : "expand"
              }`,
              "candidate"
            );
          }}
          position="absolute"
          right={6}
          background="white"
          zIndex="1"
          size="sm"
          leftIcon={
            notesExpanded ? (
              <HiChevronDoubleRight size={20} />
            ) : (
              <HiChevronDoubleLeft size={20} />
            )
          }
        >
          Summary
        </Button>
        <Flex direction="row" marginRight="200px">
          {summaryExists && (
            <Flex
              flexDir="row"
              alignItems="center"
              gap={2}
              marginRight={2}
              marginLeft={8}
              opacity={notesExpanded ? 1 : 0}
              transition="opacity 0.3s"
            >
              <Text
                fontSize="sm"
                color="gray.600"
                fontWeight={400}
                whiteSpace="nowrap"
              >
                Template
              </Text>
              <SummaryTemplateSelect
                handleChangeTemplate={handleChangeTemplate}
                selectedTemplate={selectedTemplate}
                templates={templates || []}
              />
              {templates && currentTemplate && (
                <SummaryEditTemplateButton
                  currentTemplateId={currentTemplate.id}
                  candidateId={candidateId}
                  positionId={positionId}
                />
              )}
            </Flex>
          )}
          {summaryExists && (
            <SummaryCopyNotesButton
              isDisabled={!currentTemplate}
              opacity={notesExpanded ? 1 : 0}
              transition="opacity 0.3s"
              copyAll={copyAll}
            />
          )}
        </Flex>
      </Flex>
      <ColumnHeaderBorder mx={6} />
      {!summaryExists && !summaryOrTemplateLoading && (
        <Flex flexDir="column" gap={1} width="100%" pb={5} px={6}>
          <Flex
            flexDir="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Text
              fontSize="sm"
              color="gray.600"
              fontWeight={400}
              whiteSpace="nowrap"
            >
              Template
            </Text>
          </Flex>
          <Flex flexDir="row" alignItems="center" gap={2}>
            <SummaryTemplateSelect
              handleChangeTemplate={handleChangeTemplate}
              selectedTemplate={selectedTemplate}
              templates={templates || []}
              width="100%"
            />
            {templates && currentTemplate && !isBaseOrSmall && (
              <SummaryEditTemplateButton
                currentTemplateId={currentTemplate.id}
                candidateId={candidateId}
                positionId={positionId}
              />
            )}

            {summaryExists && <SummaryCopyNotesButton copyAll={copyAll} />}
          </Flex>
        </Flex>
      )}
      {children}
    </Flex>
  );
};

/*
 * Wrapper for V2 candidate summary tab for smaller screens.
 * This is for when the topics and notes are displayed in the same column.
 */
//* *
const CombinedTopicSummaryWrapper = ({
  children,
  candidateId,
  selectedTemplate,
  handleChangeTemplate,
  templates,
  summaryExists,
  copyAll,
  positionId,
  isExtension,
  summaryOrTemplateLoading,
}: TopicSummaryWrapperProps): JSX.Element => {
  // if bigger than small
  const currentBreakPoint = useCurrentBreakpoint();
  const isBaseOrSmall = ["base", "sm"].includes(currentBreakPoint);
  const currentTemplate = templates.find(
    (item) => item.id === selectedTemplate
  );
  return (
    <Flex flexDir="column" overflow="hidden" width="100%">
      {!isBaseOrSmall && (
        <Box px={6}>
          <Flex flexDir="row" alignItems="center">
            <ColumnHeading
              data-tour-id="candidate-summary-column-heading-combined"
              iconType={HiOutlineClipboardList}
              title="Topics & Summary"
              tooltipIcon={
                <SummaryTooltipIcon iconProps={{ mt: "3px", mb: "0" }} />
              }
            />
          </Flex>
          <ColumnHeaderBorder />
        </Box>
      )}
      {!summaryOrTemplateLoading && (
        <Flex flexDir="row" alignItems="center" px={6}>
          <Flex flexDir="column" gap={1} width="100%" pb={5}>
            <Flex
              flexDir="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Text
                fontSize="sm"
                color="gray.600"
                fontWeight={400}
                whiteSpace="nowrap"
              >
                Template
              </Text>
            </Flex>
            <Flex flexDir="row" alignItems="center" gap={2}>
              <SummaryTemplateSelect
                handleChangeTemplate={handleChangeTemplate}
                selectedTemplate={selectedTemplate}
                templates={templates || []}
                width="100%"
              />
              {templates && currentTemplate && !isBaseOrSmall && (
                <SummaryEditTemplateButton
                  currentTemplateId={currentTemplate.id}
                  candidateId={candidateId}
                  positionId={positionId}
                  editInNewTab={isExtension}
                />
              )}

              {summaryExists && <SummaryCopyNotesButton copyAll={copyAll} />}
            </Flex>
          </Flex>
        </Flex>
      )}
      <Box px={6} overflow="auto">
        {children}
      </Box>
    </Flex>
  );
};

const reshapeNotes = (data: CandidateSummary): Topic[] => {
  const { notes } = data;
  const sortedNotes = [...notes].sort((a, b) => a.position - b.position);

  // Convert sorted notes into topics, subtopics, notes, and citations
  const topics: Topic[] = [];
  let currentTopic: Topic | null = null;
  let currentSubtopic: SubTopic | null = null;
  let currentNote: Note | null = null;
  sortedNotes.forEach((note) => {
    if (currentTopic === null || currentTopic.name !== note.topic) {
      currentTopic = {
        name: note.topic || "",
        subTopics: [],
      };
      topics.push(currentTopic);
    }
    if (currentSubtopic === null || currentSubtopic.name !== note.subtopic) {
      if (
        note.citations.length === 0 &&
        (note.subtopic === "Other" || note.subtopic?.trim() === "")
      ) {
        return;
      }
      if (note.subtopic === "" && note.citations.length === 0) {
        return;
      }
      currentSubtopic = {
        name: note.subtopic || "",
        notes: [],
      };
      currentTopic.subTopics.push(currentSubtopic);
    }
    currentNote = {
      id: note.id,
      text: note.text,
      citations: note.citations.map((citation) => ({
        id: citation.id,
        callId: citation.callId,
        time: citation.time,
      })),
    };
    currentSubtopic.notes.push(currentNote);
  });

  return topics;
};

const EmptyStateGenerate: React.FC<
  {
    selectedTemplate: string;
    generateSummary?: (templateId: string) => void;
  } & BoxProps
> = ({ selectedTemplate, generateSummary, ...boxProps }) => {
  return (
    <SummaryEmptyStateWrapper {...boxProps}>
      <EmptyAIIcon mt={-10} mb={-8} maxW="161px" />
      <Text align="center" maxW="400px" textAlign="center" fontSize="sm">
        Click generate to get summarized notes about the candidate from all
        their interviews.
      </Text>
      <Button
        flexShrink={0}
        size="md"
        colorScheme="purple"
        bg="purple.450"
        onClick={() => {
          if (selectedTemplate && generateSummary) {
            generateSummary(selectedTemplate);
          }
        }}
        leftIcon={<Icon boxSize={6} as={HiOutlineSparkles} />}
      >
        Generate summary
      </Button>
      <Text maxW="400px" textAlign="center" fontSize="sm">
        This may take up to 30 seconds, depending on the number of interviews.
      </Text>
    </SummaryEmptyStateWrapper>
  );
};

export default SummaryTabContent;
